import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import { ELYSIUM_TRADE, sameTradeAddress, tradeConfigKey, isMerkleV4 } from "../../../config/trade";
import { configuredElysiumReadProvider, configuredElysiumRpcUrl } from "../../../wallet/elysium-rpc";
import { readNativeV3Balances, type NativeV3Balances } from "../../../wallet/native-v3-balances";
import type { ConnectedTradeInput, TradeBalanceStatus } from "../types";

export type TradeBalanceRead = {
  balances: NativeV3Balances | null;
  balanceStatus: TradeBalanceStatus;
  refreshBalances(): void;
};
type BalanceConnection = Pick<ConnectedTradeInput, "wallet" | "tracker" | "trade" | "bootstrap" | "sourceToken">;
const freshFor = 30_000;

// The general wallet revision includes SIWE session changes. Balances
// follow only connection identity/chain; every intermediate change still fences A -> B -> A.
export function observeTradeWallet(controller: BalanceConnection["wallet"]) {
  const read = () => {
    const { account, chainId } = controller.getSnapshot();
    return { account, chainId, provider: controller.getProvider(), identity: controller.getIdentityRevision() };
  };
  let snapshot = { ...read(), revision: 0 };
  const getSnapshot = () => {
    const next = read();
    if (next.identity !== snapshot.identity || next.account !== snapshot.account ||
      next.chainId !== snapshot.chainId || next.provider !== snapshot.provider) snapshot = { ...next, revision: snapshot.revision + 1 };
    return snapshot;
  };
  return { getSnapshot, subscribe: (notify: () => void) => controller.subscribe(() => {
    const previous = snapshot;
    if (getSnapshot() !== previous) notify();
  }) };
}

/** Owned above Routes: navigation changes polling, never the query or its request. */
export function useTradeBalances(connection: BalanceConnection, client: QueryClient, active: boolean): TradeBalanceRead {
  const scope = useMemo(() => observeTradeWallet(connection.wallet), [connection.wallet]);
  const wallet = useSyncExternalStore(scope.subscribe, scope.getSnapshot, scope.getSnapshot);
  const transactions = useSyncExternalStore(connection.tracker.subscribe, connection.tracker.getSnapshot, connection.tracker.getSnapshot);
  const seenConfirmations = useRef(new Set(transactions.items.filter(item => item.phase === "CONFIRMED").map(item => item.id)));
  const trade = connection.trade ?? ELYSIUM_TRADE;
  const key = JSON.stringify([connection.trade === null ? "unavailable" : tradeConfigKey(trade), wallet.revision, wallet.account, wallet.chainId,
    configuredElysiumRpcUrl()?.href]);
  const latestKey = useRef(key); latestKey.current = key;
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  const available = Boolean(connection.trade !== null && wallet.account && wallet.chainId === ELYSIUM_TRADE.chainId &&
    trade.chainId === ELYSIUM_TRADE.chainId && (isMerkleV4(trade) || sameTradeAddress(trade.token, ELYSIUM_TRADE.token)));
  const options = useMemo(() => queryOptions({
    queryKey: ["trade-balances", key],
    staleTime: freshFor, gcTime: 5 * 60_000, retry: false, networkMode: "always",
    refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false,
    queryFn: async ({ signal }) => {
      const guard = () => {
        signal.throwIfAborted();
        if (key !== latestKey.current || wallet !== scope.getSnapshot()) throw new Error("TRADE_CONTEXT_CHANGED");
      };
      guard();
      const provider = configuredElysiumReadProvider(signal) ?? wallet.provider;
      if (!provider || !wallet.account) throw new Error("ELYSIUM_RPC_UNAVAILABLE");
      const data = await readNativeV3Balances(provider, wallet.account, guard, { signal }, trade);
      guard(); return data;
    },
  }), [key, scope, wallet, trade]);
  const query = useQuery({ ...options, enabled: available && visible &&
    (active || client.getQueryData(options.queryKey) === undefined) }, client);
  const load = useCallback((force = false) => {
    if (!available || document.visibilityState === "hidden" || key !== latestKey.current || wallet !== scope.getSnapshot()) return;
    // fetchQuery shares a pending read; timers and route changes cannot duplicate it.
    void client.fetchQuery({ ...options, ...(force ? { staleTime: 0 } : {}) }).catch(() => {
      // Query state owns read errors and retains the last successful balance.
    });
  }, [available, client, options, key, scope, wallet]);
  const refreshBalances = useCallback(() => {
    // A confirmed transaction supersedes an in-flight snapshot. Cancel it before
    // invalidating so a late pre-confirmation result cannot repopulate the cache.
    void client.cancelQueries({ queryKey: options.queryKey, exact: true }).catch(() => {});
    void client.invalidateQueries({ queryKey: options.queryKey, exact: true, refetchType: "none" }).catch(() => {});
    load(true);
  }, [client, options, load]);

  useEffect(() => {
    let changed = false;
    for (const record of transactions.items) {
      if (record.phase !== "CONFIRMED" || seenConfirmations.current.has(record.id)) continue;
      seenConfirmations.current.add(record.id);
      if (sameTradeAddress(record.account, wallet.account ?? "") && record.chainId === wallet.chainId &&
        sameTradeAddress(record.sourceToken, trade.token) &&
        record.environment === (isMerkleV4(trade) ? `${trade.environment}:merkle-v4:${trade.poolId}` : `${trade.environment}:elysium-v3`)) changed = true;
    }
    if (changed) refreshBalances();
  }, [transactions.items, wallet.account, wallet.chainId, trade, refreshBalances]);

  useEffect(() => {
    const onVisibility = () => {
      setVisible(document.visibilityState !== "hidden");
      if (active) load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [active, load]);
  useEffect(() => {
    if (!active || !visible) return;
    load(); // Re-entering Trade revalidates only when the cached result is stale.
    const timer = setInterval(() => load(true), 15_000);
    return () => clearInterval(timer);
  }, [active, visible, load]);
  useEffect(() => () => {
    void client.cancelQueries({ queryKey: options.queryKey, exact: true }).catch(() => {});
    client.removeQueries({ queryKey: options.queryKey, exact: true });
  }, [client, options]);

  const data = available ? query.data ?? null : null;
  const stale = query.isError || client.getQueryState(options.queryKey)?.isInvalidated ||
    Date.now() - query.dataUpdatedAt >= freshFor;
  return { balances: data, refreshBalances, balanceStatus: !available ? "idle" :
    data ? stale ? "stale" : "ready" : query.isError ? "unavailable" : "loading" };
}
